import type { ErrorBoundaryProps as ExpoErrorBoundaryProps } from 'expo-router'
import { router, usePathname } from 'expo-router'
import { Component, type ReactNode, useEffect } from 'react'
import { StyleSheet, View } from 'react-native'
import { Text, useTheme } from 'react-native-paper'
import { AuthRequiredError } from '@/lib/auth'
import { signInUrl } from '@/lib/auth-redirect'
import { HIDE_ERRORS } from '@/lib/config'
import { captureException, OrgAccessError } from '@/lib/errors'
import { AccessDeniedScreen } from '@/ui/AccessDeniedScreen'
import { Button } from '@/ui/Button'

function AuthRedirect() {
    const theme = useTheme()
    const pathname = usePathname()

    useEffect(() => {
        router.replace(signInUrl(pathname) as '/')
    }, [pathname])

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            <Text variant="bodyMedium" style={{ color: theme.colors.onBackground }}>
                Redirecting to sign in...
            </Text>
        </View>
    )
}

function ErrorFallback({ error, retry }: { error: Error; retry: () => void }) {
    const theme = useTheme()

    useEffect(() => {
        if (!(error instanceof AuthRequiredError) && !(error instanceof OrgAccessError)) {
            captureException('ErrorBoundary', error)
        }
    }, [error])

    if (error instanceof AuthRequiredError) {
        return <AuthRedirect />
    }

    if (error instanceof OrgAccessError) {
        return <AccessDeniedScreen />
    }

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            <Text
                variant="headlineMedium"
                style={[styles.title, { color: theme.colors.onBackground }]}
            >
                Oops, something went wrong
            </Text>
            <Text
                variant="bodyMedium"
                style={[styles.message, { color: theme.colors.onSurfaceVariant }]}
            >
                We've been notified and are looking into it.
            </Text>
            {__DEV__ && !HIDE_ERRORS && (
                <Text variant="bodySmall" style={[styles.error, { color: theme.colors.error }]}>
                    {error.toString()}
                </Text>
            )}
            <Button title="Try again" mode="contained" onPress={retry} style={styles.button} />
        </View>
    )
}

export function ErrorBoundary({ error, retry }: ExpoErrorBoundaryProps) {
    return <ErrorFallback error={error} retry={retry} />
}

interface ErrorBoundaryWrapperProps {
    children: ReactNode
}

interface ErrorBoundaryWrapperState {
    error: Error | null
}

export class ErrorBoundaryWrapper extends Component<
    ErrorBoundaryWrapperProps,
    ErrorBoundaryWrapperState
> {
    state: ErrorBoundaryWrapperState = { error: null }

    static getDerivedStateFromError(error: Error) {
        return { error }
    }

    retry = () => {
        this.setState({ error: null })
    }

    render() {
        if (this.state.error) {
            return <ErrorFallback error={this.state.error} retry={this.retry} />
        }
        return this.props.children
    }
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    title: {
        marginBottom: 12,
        textAlign: 'center',
    },
    message: {
        marginBottom: 20,
        textAlign: 'center',
    },
    error: {
        marginBottom: 20,
        textAlign: 'center',
        fontFamily: 'monospace',
    },
    button: {
        marginTop: 8,
    },
})
